/**
 * FRONT-2473: what EDDY may use WHILE BUILDING — the session's three built-in
 * capability groups (FRONT-2219), now in Configuration behind one master
 * switch instead of three menu checkmarks. Pure — unit tested.
 */
import type { CodingCapability } from '~/hooks/mutations/use-coding-mutations';

export type BuilderCaps = Record<CodingCapability, boolean>;

/** Names match the builder prompt's own labels (src/coding/prompt.ts), so EDDY
 *  and the person call each switch the same thing. */
export const BUILDER_CAPABILITIES: ReadonlyArray<{ key: CodingCapability; name: string; description: string }> = [
  {
    key: 'web',
    name: 'Web search & fetch',
    description: 'Look things up on the live web (current docs, versions, prices) and read a page by its address.',
  },
  {
    key: 'knowledge',
    name: 'Knowledge bases',
    description: "Search your company's knowledge bases and name the document it used.",
  },
  {
    key: 'agents',
    name: 'Delegate to my agents',
    description: 'Hand specialist work to your agents, e.g. the Atlassian agent for Jira.',
  },
];

/** A session row's switches; a missing value reads as on, matching the server defaults. */
export function builderCapsFrom(session: {
  webToolsEnabled?: boolean;
  knowledgeToolsEnabled?: boolean;
  agentToolsEnabled?: boolean;
} | null | undefined): BuilderCaps {
  return {
    web: session?.webToolsEnabled ?? true,
    knowledge: session?.knowledgeToolsEnabled ?? true,
    agents: session?.agentToolsEnabled ?? true,
  };
}

/** The master switch reads on while anything is on. */
export function masterOn(caps: BuilderCaps): boolean {
  return caps.web || caps.knowledge || caps.agents;
}

/** Flipping the master sets all three at once: all off while anything is on, else all on. */
export function masterToggle(caps: BuilderCaps): BuilderCaps {
  const next = !masterOn(caps);
  return { web: next, knowledge: next, agents: next };
}

export function masterStatus(caps: BuilderCaps): string {
  const on = BUILDER_CAPABILITIES.filter((c) => caps[c.key]).length;
  if (on === 0) return 'Off';
  if (on === BUILDER_CAPABILITIES.length) return 'All on';
  return `${on} of ${BUILDER_CAPABILITIES.length} on`;
}
